"use client";

import React, { useState } from "react";
import { REBOTT_API_BASE } from '@/config';
import Image from "next/image";
import { Typography, Input, Textarea, Button } from "@material-tailwind/react";
import { XMarkIcon, PaperAirplaneIcon, EnvelopeIcon } from "@heroicons/react/24/solid";
import { MotionDiv, MotionSection, MotionForm, Variants } from "@/utils/clientMotion";

  // Header variants: ข้อความเลื่อนลงมา
  const headerVariants: Variants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const slideLeft: Variants = {
    hidden: { x: -100, opacity: 0 },
    visible: { x: 0,    opacity: 1, transition: { type: "spring", stiffness: 120 } },
  };

  const slideRight: Variants = {
    hidden: { x:  100, opacity: 0 },
    visible: { x:   0,  opacity: 1, transition: { type: "spring", stiffness: 120 } },
  };

  const slideDown: Variants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 120, damping: 12 },
    },
  };

export default function ContactUs() {
  // state เก็บค่าฟอร์ม
  const [name, setName] = useState("");
  const [tel, setTel] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOpenToast(true);
    setLoading(true);
    setTimeout(() => setOpenToast(false), 3000);

    try {
      const res = await fetch(`${REBOTT_API_BASE}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, tel, email, title, message }),
      });
      if (!res.ok) throw new Error("Network response was not ok");
      
      setShowModal(true);
 
      setName("");
      setTel("");
      setEmail("");
      setTitle("");
      setMessage("");
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการส่งข้อความ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    {/* แสดง toast เมื่อ openToast = true */}
    {showModal && (
      // <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <MotionDiv
      initial="hidden"
      animate="visible"
      variants={slideDown}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      >  
        <div className="relative bg-white w-80 max-w-full p-8 rounded-lg shadow-xl">
          <button
            onClick={() => setShowModal(false)}
            aria-label="ปิด"
            className="absolute top-1 right-0
            w-10 h-10
            text-gray-400 
            hover:text-gray-800
            focus:outline-none focus:ring-2 focus:ring-blue-400
            "
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <Typography {...({} as any)}
            variant="lead"
            className="text-center text-md mb-2 tracking-wide"
          >
            ส่งข้อความถึงเราสำเร็จแล้ว! 🎉
          </Typography>

          <EnvelopeIcon
            className="mx-auto block h-8 w-8 text-[#2998E2]"
          />
        </div>
      </MotionDiv>
    )}
      <section className="pt-20 pb-20 px-1 lg:pt-20 lg:pb-20 bg-[#F5FCFE] w-full">
        <div className="max-w-5xl mx-auto px-5">
          <MotionDiv
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={headerVariants}
          className="text-center mb-6"
        >
            <Typography {...({} as any)}  variant="h3" className="font-bold text-[#2998E2]">
              ติดต่อเรา
            </Typography>
            <Typography {...({} as any)} variant="lead" className="mx-auto text-lg text-gray-800 tracking-wide">
              มาร่วมเป็นส่วนหนึ่งในการช่วยลดขยะพลาสติก ติดตั้งตู้ <span className="text-[#2998E2]">Rebott</span> กับเราได้ทันที!
            </Typography>
          </MotionDiv>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <MotionDiv
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.3 }}
                variants={slideLeft}
                className="overflow-hidden rounded-xl shadow-md w-full aspect-[400/320]"
              >
              <Image
                src="/image/contactus_banner.webp"
                alt="Rebott Contact"
                width={400}
                height={320}
                className="w-full h-full object-cover"
              />
            </MotionDiv>

            {/* <form onSubmit={handleSubmit} className="space-y-3"> */}
            <MotionForm
              onSubmit={handleSubmit}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }}
              variants={slideRight}
              className="space-y-3"
            >
              <Typography {...({} as any)} as="h4" variant="h5" className="mt-2 mb-5 text-center text-gray-800">
                สนใจติดตั้ง/ติดต่อสอบถาม
              </Typography>
              <label htmlFor="input-name" className="sr-only">
                ชื่อ-นามสกุล หรือ ชื่อบริษัท
              </label>
              <Input {...({} as any)} 
                id="input-name"
                label="ชื่อ-นามสกุล หรือ ชื่อบริษัท"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                required
                className="bg-white"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label htmlFor="input-tel" className="sr-only">
                  เบอร์โทรศัพท์
                </label>
                <Input {...({} as any)} 
                  id="input-tel"
                  type="tel"
                  label="เบอร์โทรศัพท์"
                  value={tel}
                  onChange={(e) => setTel(e.currentTarget.value)}
                  required
                  className="bg-white"
                />
                <label htmlFor="input-email" className="sr-only">
                  อีเมล
                </label>
                <Input {...({} as any)} 
                  id="input-email"
                  type="email"              
                  label="อีเมล" 
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                  className="bg-white"
                />
              </div>
              <label htmlFor="input-title" className="sr-only">
                หัวข้อ
              </label>
              <Input {...({} as any)} 
                id="input-title"              
                label="หัวข้อ"
                value={title}
                onChange={(e) => setTitle(e.currentTarget.value)}
                required
                className="bg-white"
              />
              <label htmlFor="textarea-message" className="sr-only">
                รายละเอียด
              </label>
              <Textarea {...({} as any)} 
                id="textarea-message"              
                label="รายละเอียด"
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                className="!h-[100px] bg-white"
              />

              <Button {...({} as any)} 
                type="submit"
                disabled={loading}
                className="flex items-center justify-center gap-2 bg-[#2998E2] hover:bg-[#2083c7] text-md font-base tracking-wide"
                fullWidth
              >
                {loading ? (
                  <>
                    กำลังส่งข้อความ...
                    {/* ใช้ spinner icon */}
                    <PaperAirplaneIcon className="h-5 w-5 animate-bounce " /> 
                  </>
                ) : (
                  <>
                    ส่งข้อความถึงเรา
                    <PaperAirplaneIcon className="h-5 w-5" />
                  </>
                )}
              </Button>
            </MotionForm>
          </div>
        </div>
      </section>
    </>
  );
}
